import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { secondsToTimeText } from '../../../../../platform/ux/Advertiser/utils/secondsToTimeText';
import { getReadableDomain, isHtml5Creative, getDspFromImpression } from '../../../../../platform/ux/Advertiser/details/components/utils';
import { colors } from '../../../../utils/constants/listOfColors';

const AdvertiserSessionsController = (props) => {
  const { signedURL, advertiser, setReport, setCreatives, creatives } = props;
  const [reportDataSummary, setReportData] = useState({
    byDates: [],
    signups: [],
    summary: {
      signups: 0,
    },
  });
  const [reportDataRawSummary, setReportRawData] = useState();
  const [topCreatives, setTopCreatives] = useState([]);
  const [topImpressions, setTopImpressions] = useState([]);
  const [topInventoryTypes, setTopInventoryTypes] = useState([]);
  const [reportDataSummaryLoading, setReportLoading] = useState(true);

  useEffect(() => {
    if (signedURL) {
      const s3UrlSummary = new URL(signedURL.getSignedURL);
      fetch(s3UrlSummary).then(async (response) => {
        if (response && response.ok) {
          try {
            await response.json().then((json) => {
              if (json) {
                let report = [];
                if (json.byDate) {
                  report.push({
                    name: 'Session - By Dates',
                    value: json.byDate,
                  });
                }
                if (json.sessionList) {
                  report.push({
                    name: 'Session List',
                    value: json.sessionList.map((s) => ({
                      'Session ID': s.sessionId,
                      'Start Time': s.sessionStart,
                      'End Time': s.sessionEnd,
                      'Duration (s)': secondsToTimeText(s.durationInSeconds),
                      'Page Views': s.pageViews,
                      'Landing Page': s.landingPage,
                      'Exit Page': s.landingPage,
                      Device: s.device,
                      'Attribution Method': Object.keys(s.attributionMethod)[0]
                        .toLowerCase()
                        .replace(/_/g, ' ') // Replace underscores with spaces
                        .replace(/\b\w/g, (c) => c.toUpperCase()), // Capitalize first letter of each word,
                      Journey: JSON.stringify(s.journey),
                    })),
                  });
                }
                setReport(report);
              }

              let creativesRaw = [];
              if (json.sessionList) {
                json.sessionList = json.sessionList.map((s) => {
                  return {
                    ...s,
                    impressionsList: (s.impressionsList || []).map((i) => {
                      const maskCreative = creatives.find((c) => c.creativeID === i.CREATIVEID);

                      const creativeExists = creatives.findIndex((c) => c.name === i.CREATIVEID);
                      if (creativeExists === -1) {
                        creativesRaw.push({ name: i.CREATIVEID });
                      }

                      return {
                        ...i,
                        CREATIVEURL: maskCreative ? maskCreative.url : (i.CREATIVEURL || ''),
                        CREATIVEID: maskCreative ? maskCreative.name : i.CREATIVEID,
                      };
                    }),
                  };
                });
              }

              setCreatives(creativesRaw);

              const { sortedCreatives, sortedImpressionDomains, sortedInventoryTypes } =
                topChartData(json.sessionList);

              setTopCreatives(sortedCreatives);
              setTopImpressions(sortedImpressionDomains);
              setTopInventoryTypes(sortedInventoryTypes);

              setReportData(json);
              setReportRawData(json);
              setReportLoading(false);
            });
          } catch (err) {
            console.log(err);
            setReportLoading(false);
          }
        } else {
          setReportLoading(false);
        }
      });
    } else {
      setReport([]);
      setReportLoading(false);
    }
  }, [creatives, setCreatives, setReport, signedURL]);

  const changeDatePicker = (dates) => {
    const datePickerStartDate = dates[0].format('YYYY-MM-DD');
    const datePickerEndDate = dates[1].format('YYYY-MM-DD');

    if (!reportDataRawSummary) {
      return;
    }

    const { byDate, sessionList } = reportDataRawSummary;

    const filteredByDates = byDate.filter(
      (date) =>
        moment(date.date).isSameOrBefore(datePickerEndDate) &&
        moment(date.date).isSameOrAfter(datePickerStartDate)
    );
    const filteredSessions = sessionList.filter(
      (sess) =>
        moment(sess.sessionStart).isSameOrAfter(datePickerStartDate, 'day') &&
        moment(sess.sessionStart).isSameOrBefore(datePickerEndDate, 'day')
    );

    let summary = {
      byDate: filteredByDates,
      deviceBreakdown: filteredSessions.reduce(
        (acc, session) => {
          if (session.device === 'Mobile') {
            acc.mobile += 1;
          } else if (session.device === 'Tablet') {
            acc.tablet += 1;
          } else if (session.device === 'Desktop') {
            acc.desktop += 1;
          }

          return acc;
        },
        {
          mobile: 0,
          tablet: 0,
          desktop: 0,
        }
      ),
      sessions: filteredSessions.length,
      sessionList: filteredSessions,
      ...filteredByDates.reduce(
        (acc, curr) => {
          acc.impressions += curr.attributedImpressions || 0;
          acc.totalSessionDuration += curr.durationInSeconds || 0;
          acc.totalPageViews += curr.pageViews || 0;
          acc.averageSessionDuration = acc.totalSessionDuration / filteredSessions.length || 0;
          acc.pagesPerSession = acc.totalPageViews / filteredSessions.length || 0;
          acc.totalImpressions += curr.totalImpressions || 0;
          return acc;
        },
        {
          impressions: 0,
          averageSessionDuration: 0,
          pagesPerSession: 0,
          totalImpressions: 0,
          totalSessionDuration: 0,
          totalPageViews: 0,
        }
      ),
    };

    const { sortedCreatives, sortedImpressionDomains, sortedInventoryTypes } =
      topChartData(filteredSessions);

    setTopCreatives(sortedCreatives);
    setTopImpressions(sortedImpressionDomains);
    setTopInventoryTypes(sortedInventoryTypes);

    let report = [];
    if (filteredByDates) {
      report.push({
        name: 'Session - By Dates',
        value: filteredByDates,
      });
    }
    if (filteredSessions) {
      report.push({
        name: 'Session List',
        value: filteredSessions.map((s) => ({
          'Session ID': s.sessionId,
          'Start Time': s.sessionStart,
          'End Time': s.sessionEnd,
          'Duration (s)': secondsToTimeText(s.durationInSeconds),
          'Page Views': s.pageViews,
          'Landing Page': s.landingPage,
          'Exit Page': s.landingPage,
          Device: s.device,
          'Attribution Method': Object.keys(s.attributionMethod)[0]
            .toLowerCase()
            .replace(/_/g, ' ') // Replace underscores with spaces
            .replace(/\b\w/g, (c) => c.toUpperCase()), // Capitalize first letter of each word,
          Journey: JSON.stringify(s.journey),
        })),
      });
    }
    setReport(report);

    setReportData(summary);
  };

  const topChartData = (sessionList) => {
    const creatives = [];
    const impressionDomains = [];
    const inventoryTypes = [];
    let index = 0;

    (sessionList || []).forEach((session) => {
      (session.impressionsList || []).forEach((impression) => {
        index++;

        const { impressionDomain, inventoryType } = getReadableDomain(impression, null);
        const dsp = getDspFromImpression(impression);
        const isHtml5 = isHtml5Creative(impression.IMPRESSION_PAGE_URL);

        const creativeExists = creatives.findIndex((c) => c.name === impression.CREATIVEID);
        if (creativeExists === -1) {
          creatives.push({
            name: impression.CREATIVEID,
            impressions: 1,
            sessions: 1,
            sessionsList: [session.sessionId],
            color: colors[index],
            url: impression.CREATIVEURL,
            isHtml5: isHtml5 || false,
            dsp: dsp || 'Unknown',
          });
        } else {
          const sessionExists = creatives[creativeExists].sessionsList.findIndex(
            (s) => s === session.sessionId
          );
          if (sessionExists === -1) {
            creatives[creativeExists].sessionsList.push(session.sessionId);
            creatives[creativeExists].sessions += 1;
          }
          creatives[creativeExists].impressions += 1;
          if (isHtml5) {
            creatives[creativeExists].isHtml5 = true;
          }
          if (dsp !== 'Unknown' && (!creatives[creativeExists].dsp || creatives[creativeExists].dsp === 'Unknown')) {
            creatives[creativeExists].dsp = dsp;
          }
        }

        const impressionExists = impressionDomains.findIndex((i) => i.name === impressionDomain);
        if (impressionExists === -1) {
          impressionDomains.push({
            name: impressionDomain,
            unformatted: impressionDomain,
            sessions: 1,
            type: inventoryType,
            sessionsList: [session.sessionId],
            url: null,
            color: colors[index],
          });
        } else {
          const sessionExists = impressionDomains[impressionExists].sessionsList.findIndex(
            (s) => s === session.sessionId
          );
          if (sessionExists === -1) {
            impressionDomains[impressionExists].sessionsList.push(session.sessionId);
            impressionDomains[impressionExists].sessions += 1;
          }
        }

        const inventoryTypeExists = inventoryTypes.findIndex((i) => i.type === inventoryType);
        if (inventoryTypeExists === -1) {
          inventoryTypes.push({
            type: inventoryType,
            impressions: 1,
            sessions: 1,
            sessionsList: [session.sessionId],
            color: colors[index],
          });
        } else {
          const sessionExists = inventoryTypes[inventoryTypeExists].sessionsList.findIndex(
            (s) => s === session.sessionId
          );
          if (sessionExists === -1) {
            inventoryTypes[inventoryTypeExists].sessionsList.push(session.sessionId);
            inventoryTypes[inventoryTypeExists].sessions += 1;
          }
          inventoryTypes[inventoryTypeExists].impressions += 1;
        }
      });
    });

    const sortedCreatives = creatives.sort((a, b) => b.sessions - a.sessions);
    const sortedImpressionDomains = impressionDomains.sort((a, b) => b.sessions - a.sessions);
    const sortedInventoryTypes = inventoryTypes.sort((a, b) => b.sessions - a.sessions);

    return {
      sortedCreatives,
      sortedImpressionDomains,
      sortedInventoryTypes,
    };
  };

  return React.cloneElement(props.children, {
    ...props.children.props,
    reportDataSummary,
    advertiser,
    reportDataSummaryLoading,
    changeDatePicker,
    topCreatives,
    topImpressions,
    topInventoryTypes,
  });
};

export default AdvertiserSessionsController;
